import { useContext, type FunctionComponent } from 'react';
import { buildChoicesAnswer, getChoicesAnswer } from '../../../domain/answer';
import { upsert } from '../../../domain/answer/upsert';
import Question from '../../../interfaces/question';
import { QuestionType } from '../../../interfaces/questionType';
import {
  ChoicesAnswer,
  MappedAnswer,
  ObjectId,
  SurveyPageContext,
} from '../../context/SurveyPageContext';
import ChooseMany, { ChoiceItem } from '../ChooseMany/ChooseMany';

interface QuestionFactoryProps {
  question: Question;
}

const OTHER_VALUE = 'Other';

const QuestionFactory: FunctionComponent<QuestionFactoryProps> = ({ question }) => {
  const { upsertMappedAnswers, mappedAnswers } = useContext(SurveyPageContext);

  const updateAnswer = (answer: MappedAnswer) => {
    const upsertedAnswers: Map<number, MappedAnswer> = upsert({ answer, mappedAnswers });
    upsertMappedAnswers(upsertedAnswers);
  };

  switch (question.type) {
    case QuestionType.CHOOSE_MANY: {
      const answer = getChoicesAnswer(mappedAnswers, question.id);
      const alreadyOnAnswer = (id: number) => !!answer?.choices.find((c) => c.id === id);

      const onChange = (
        choiceId: number,
        newValue: boolean,
        isOtherChoiceEnabled: boolean,
        newTextValue: string,
        setValueOtherChoice: (value: string) => void,
      ) => {
        const questionId = question.id;
        let choices: ObjectId[];
        let textValue: string;
        if (newValue) {
          const existentValue = alreadyOnAnswer(choiceId);
          choices = existentValue
            ? [...(answer?.choices || [])]
            : [...(answer?.choices || []), { id: choiceId }];
          textValue = newTextValue;
        } else {
          textValue = isOtherChoiceEnabled ? '' : newTextValue;
          choices = [...(answer?.choices.filter((c) => c.id !== choiceId) || [])];
        }
        setValueOtherChoice(textValue);
        const newAnswer: ChoicesAnswer = buildChoicesAnswer({
          questionId,
          choices,
          newTextValue: textValue,
        });
        updateAnswer(newAnswer);
      };

      const { enableOtherChoice } = question;
      const choiceItems: ChoiceItem[] = question.choices.map((c) => ({
        id: c.id,
        label: c.value || '',
        checked: alreadyOnAnswer(c.id),
        otherChoice: enableOtherChoice && c.value === OTHER_VALUE,
        explanation: c.explanation,
      }));
      const amountOfAnswers = getChoicesAnswer(mappedAnswers, question.id)?.choices?.length || 0;
      const isMaximumReached = !!question.maxRequired && question.maxRequired <= amountOfAnswers;
      return (
        <ChooseMany
          choiceItems={choiceItems}
          onChange={onChange}
          currentValueOtherChoice={answer?.value}
          isMaximumReached={isMaximumReached}
        />
      );
    }
    default:
      return <></>;
  }
};

export default QuestionFactory;
